import { useCallback, useEffect, useRef, useState } from "react"

export type ToastTone = "gain" | "loss" | "neutral"

export interface Toast {
  id: number
  text: string
  tone: ToastTone
}

const TOAST_LIFETIME_MS = 6000

/**
 * Transient confirmations for things the game used to change silently —
 * knowledge learned, trust raised, Command Points granted, coin taken. Each
 * one is individually minor, which is exactly why they were easy to miss:
 * a player who gains ten Command Points from a muster and never sees it said
 * anywhere has no idea their army capacity just tripled.
 */
export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(0)
  const timers = useRef<number[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (text: string, tone: ToastTone = "neutral") => {
      const id = nextId.current++
      setToasts((current) => [...current, { id, text, tone }])
      const timer = window.setTimeout(() => dismiss(id), TOAST_LIFETIME_MS)
      timers.current.push(timer)
    },
    [dismiss],
  )

  // Timers outlive the toasts they belong to if the component unmounts first.
  useEffect(() => {
    const pending = timers.current
    return () => pending.forEach((timer) => window.clearTimeout(timer))
  }, [])

  return { toasts, push, dismiss }
}
